import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { profileStore } from '../stores/profileStore'
import { gamificationStore } from '../stores/gamificationStore'
import { authStore } from '../stores/authStore'
import Emoji from '../components/Emoji'
import Card from '../components/Card'
import { characters } from '../utils/sets'

// Common flags
const flags = ['🇪🇸', '🇺🇸', '🇬🇧', '🇫🇷', '🇩🇪', '🇮🇹', '🇵🇹', '🇧🇷', '🇲🇽', '🇦🇷', '🇨🇴', '🇯🇵', '🇰🇷', '🇨🇳']

const Profile = observer(() => {
  const { t } = useTranslation()
  const [isEditingName, setIsEditingName] = useState(false)
  const [tempName, setTempName] = useState(profileStore.username || '')

  const handleSaveName = () => {
    if (tempName.trim()) {
      profileStore.setName(tempName.trim())
    }
    setIsEditingName(false)
  }

  return (
    <div className="min-h-[calc(100vh-64px)] w-full relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-gray-100">
      {/* Background orbs sutiles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-gradient-to-br from-purple-100/30 to-pink-100/25 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-br from-pink-100/25 to-orange-100/20 rounded-full blur-[120px]" />
      </div>
      
      <div className="relative z-10 w-full max-w-2xl mx-auto space-y-6 px-4 py-6">
        {/* Header */}
        <div className="text-center">
          <Emoji emoji="👤" size="2xl" />
          <h1 className="font-title text-3xl text-gray-900 mt-2">{t('profile.title')}</h1>
        </div>

        {/* Avatar & Basic Info */}
        <Card>
          <div className="flex flex-col items-center gap-4">
            {/* Large avatar */}
            <div className="relative">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center border-2 border-gray-200 shadow-sm">
                <Emoji emoji={profileStore.emoji} size="2xl" />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1.5 border border-gray-200 shadow-sm">
                <Emoji emoji={profileStore.flag} size="sm" />
              </div>
              {/* Anonymous badge */}
              {authStore.user?.isAnonymous && (
                <div className="absolute -top-1 -left-1 bg-amber-500 rounded-full px-2 py-0.5 border border-amber-400">
                  <span className="text-[10px] text-white font-bold">👤 {t('profile.guest')}</span>
                </div>
              )}
            </div>

            {/* Username */}
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  maxLength={20}
                  className="px-3 py-2 border border-gray-300 bg-white rounded-lg font-body focus:outline-none focus:border-purple-400 text-gray-900 placeholder-gray-400 text-center"
                  placeholder={t('profile.yourName')}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                />
                <button
                  onClick={handleSaveName}
                  className="px-3 py-2 bg-green-500 text-white rounded-lg border border-green-400 hover:bg-green-600"
                >
                  ✓
                </button>
                <button
                  onClick={() => {
                    setTempName(profileStore.username)
                    setIsEditingName(false)
                  }}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-200"
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setTempName(profileStore.username)
                  setIsEditingName(true)
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border border-gray-200 group"
              >
                <span className="font-title text-xl text-gray-900">
                  {profileStore.username}
                </span>
                <Emoji emoji="✏️" size="xs" />
              </button>
            )}

            {/* Level badge */}
            <div className="flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full border border-purple-200">
              <Emoji emoji="⭐️" size="sm" />
              <span className="font-title text-lg text-purple-700">{t('common.level')} {gamificationStore.level}</span>
            </div>

            {/* Leaderboard link */}
            <Link 
              to="/leaderboard"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl hover:from-amber-200 hover:to-orange-200 transition-colors border border-amber-200"
            >
              <Emoji emoji="🏆" size="sm" />
              <span className="text-amber-800 font-medium">{t('profile.viewLeaderboard')}</span>
            </Link>
          </div>
        </Card>

        {/* Account Status Card */}
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-xl">👤</span>
              </div>
              <div>
                <p className="text-gray-900 font-medium">{t('profile.guestAccount')}</p>
                <p className="text-gray-500 text-sm">{t('profile.playingAnonymously')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-amber-600 text-xs">{t('profile.progressSavedLocally')}</p>
            </div>
          </div>
          
          {/* Future login option - commented for now */}
          {/* <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-white/70 text-sm mb-3">Link your account to save progress across devices:</p>
            <button className="w-full py-3 bg-gradient-to-r from-blue-500/30 to-purple-500/30 text-white rounded-xl font-medium hover:from-blue-500/40 hover:to-purple-500/40 transition-colors border border-blue-400/20">
              🔗 Link with Email
            </button>
          </div> */}
        </Card>

        {/* Stats */}
        <Card>
          <h2 className="font-title text-lg text-gray-900 mb-4">{t('profile.stats')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-200">
              <Emoji emoji="🔥" size="lg" />
              <p className="font-title text-2xl text-orange-600 mt-2">{profileStore.scoreForm}</p>
              <p className="text-gray-500 text-sm">{t('profile.totalScore')}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center border border-purple-200">
              <Emoji emoji="⭐️" size="lg" />
              <p className="font-title text-2xl text-purple-600 mt-2">{gamificationStore.level}</p>
              <p className="text-gray-500 text-sm">{t('common.level')}</p>
            </div>
          </div>
        </Card>

        {/* Resources */}
        <Card>
          <h2 className="font-title text-lg text-gray-900 mb-4">{t('common.resources')}</h2>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(profileStore.resources).map(([icon, amount]) => (
              <div key={icon} className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200">
                <Emoji emoji={icon} size="lg" />
                <p className="font-title text-lg text-gray-900 mt-1">{amount}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Character Selection */}
        <Card>
          <h2 className="font-title text-lg text-gray-900 mb-4">{t('profile.character')}</h2>
          <div className="flex flex-wrap justify-center gap-2">
            {Object.keys(characters).map((genderKey) => (
              <button
                key={genderKey}
                onClick={() => profileStore.setGender(genderKey)}
                className={`p-2 rounded-xl transition-all border ${
                  profileStore.gender === genderKey
                    ? 'bg-purple-100 border-purple-300 scale-110'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Emoji emoji={genderKey} size="lg" />
              </button>
            ))}
          </div>
          
          {/* Character evolution preview */}
          <div className="mt-4 flex items-center justify-center gap-1 overflow-x-auto py-2">
            {characters[profileStore.gender]?.map((char, idx) => (
              <div
                key={idx}
                className={`p-1 rounded-lg border ${
                  idx < gamificationStore.level
                    ? 'bg-green-100 border-green-300'
                    : idx === gamificationStore.level - 1
                      ? 'bg-purple-100 border-purple-300'
                      : 'bg-gray-50 border-gray-200 opacity-50'
                }`}
              >
                <Emoji emoji={char} size={idx === gamificationStore.level - 1 ? 'md' : 'sm'} />
              </div>
            ))}
          </div>
        </Card>

        {/* Flag Selection */}
        <Card>
          <h2 className="font-title text-lg text-gray-900 mb-4">{t('profile.country')}</h2>
          <div className="flex flex-wrap justify-center gap-2">
            {flags.map((flag) => (
              <button
                key={flag}
                onClick={() => profileStore.setFlag(flag)}
                className={`p-2 rounded-xl transition-all border ${
                  profileStore.flag === flag
                    ? 'bg-purple-100 border-purple-300 scale-110'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Emoji emoji={flag} size="md" />
              </button>
            ))}
          </div>
        </Card>

        {/* Settings */}
        <Card>
          <h2 className="font-title text-lg text-gray-900 mb-4">{t('profile.settings')}</h2>
          
          {/* Sound toggle */}
          <button
            onClick={() => profileStore.toggleSounds()}
            className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border border-gray-200"
          >
            <div className="flex items-center gap-3">
              <Emoji emoji={profileStore.soundsOn ? '🔊' : '🔇'} size="md" />
              <span className="font-body text-gray-900">{t('profile.soundEffects')}</span>
            </div>
            <div className={`w-12 h-6 rounded-full transition-colors ${
              profileStore.soundsOn ? 'bg-green-500' : 'bg-gray-200'
            }`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                profileStore.soundsOn ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </div>
          </button>

          {/* Reset button */}
          <button
            onClick={() => {
              if (confirm(t('profile.resetConfirm'))) {
                profileStore.reset()
              }
            }}
            className="w-full mt-3 flex items-center justify-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors border border-red-200"
          >
            <Emoji emoji="🗑️" size="sm" />
            <span className="font-body">{t('profile.resetProgress')}</span>
          </button>
        </Card>
      </div>
    </div>
  )
})

export default Profile
